/**
 * Pre-launch gate: while STORE_LOCKED is true the whole storefront is
 * hidden behind a "coming soon" screen. Only authenticated users holding
 * the admin:store permission can browse the site and verify everything
 * works before the public launch. Flip STORE_LOCKED to false to go live.
 *
 * License: AGPL-3.0-or-later (see repository LICENSE)
 */

import { useEffect, useState } from "react";
import type React from "react";

import { useAuth } from "@/authentication";
import { LoginModal } from "@/features/auth/components/login-modal";
import { SiteLoading } from "@/shared/ui/feedback/site-loading";
import logo from "@/assets/logo-cantica-divina.svg";

/**
 * Set to false at launch to open the store to everyone.
 * Always unlocked in local dev (`npm run dev`) so you can preview without
 * logging in — `import.meta.env.DEV` is false in production builds, so the
 * gate stays enforced online.
 */
const STORE_LOCKED = import.meta.env.DEV ? false : true;

export function ComingSoonGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasPermission } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(STORE_LOCKED ? null : true);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  useEffect(() => {
    if (!STORE_LOCKED) return;
    if (!isAuthenticated) {
      setAllowed(false);
      return;
    }
    let mounted = true;
    hasPermission("admin:store")
      .then((r: boolean) => mounted && setAllowed(r))
      .catch(() => mounted && setAllowed(false));
    return () => {
      mounted = false;
    };
  }, [isAuthenticated, hasPermission]);

  if (allowed) return <>{children}</>;

  // Still resolving the permission check for a logged-in user
  if (allowed === null && isAuthenticated) return <SiteLoading />;

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-8 px-6 text-center bg-background text-foreground">
      <img alt="Cantica Divina" className="h-14 md:h-20 w-auto" src={logo} />
      <div className="space-y-3">
        <h1
          className="text-3xl md:text-5xl uppercase tracking-[0.2em]"
          style={{ fontFamily: "Cinzel, Georgia, serif" }}
        >
          Coming Soon
        </h1>
        <p className="text-default-500 max-w-md mx-auto">
          The official Cantica Divina store is being forged in the abyss.
        </p>
      </div>
      <a
        className="text-sm text-primary hover:underline"
        href="https://canticadivina.com"
      >
        canticadivina.com
      </a>
      <button
        type="button"
        onClick={() => setIsLoginOpen(true)}
        className="text-xs text-default-400 hover:text-foreground transition-colors mt-8"
      >
        Admin access
      </button>
      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </div>
  );
}
